import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";

import { Button, Field, PasswordField, SelectField, StatusBadge, ToggleField } from "@fawkes/ui";

// jve-settings-integraties: the React port of the Jmix `SettingsDashboardView` integration cards
// (buildIdealIntegrationCard / buildPinIntegrationCard / buildPetbaseIntegrationCard). Renders the
// branch-context Koppelingen tile as a grid of three provider cards (iDEAL/Mollie, Pinapparaat,
// PetBase) backed by `/api/instellingen/integraties`.
//
// SECRETS ARE WRITE-ONLY. The API never returns a stored API key / password — only a masked status
// ({ configured, last4 }). The PasswordField starts empty with a masked placeholder; leaving it blank
// on save keeps the stored secret, typing a new value replaces it. So the raw secret never reaches
// the client and a save round-trips without re-entering it.

interface SecretStatus {
  configured: boolean;
  last4: string | null;
}

interface PinProviderConfig {
  apiKey: SecretStatus;
  identifier: string;
  active: boolean;
}

interface PinConfig {
  provider: string;
  ccv: PinProviderConfig;
  adyen: PinProviderConfig;
  sumup: PinProviderConfig;
}

interface IdealConfig {
  apiKey: SecretStatus;
  profileId: string;
  methods: string;
  webhook: string;
  active: boolean;
}

interface PetbaseConfig {
  username: string;
  password: SecretStatus;
  locale: string;
  active: boolean;
}

interface IntegratiesResponse {
  pin: PinConfig;
  ideal: IdealConfig;
  petbase: PetbaseConfig;
}

const PIN_PROVIDERS = ["CCV", "Adyen", "SumUp"] as const;
type PinProviderKey = (typeof PIN_PROVIDERS)[number];

// Status chip — the masked "is a secret configured" indicator that replaces the Jmix card status pill.
function SecretStatusChip({ status }: { status: SecretStatus }) {
  const t = useTranslate();
  const text = status.configured
    ? status.last4
      ? t("instellingen.integraties.status.configuredTail", { args: [status.last4] })
      : t("instellingen.integraties.status.configured")
    : t("instellingen.integraties.status.notConfigured");
  return <StatusBadge tone={status.configured ? "success" : "neutral"}>{text}</StatusBadge>;
}

// The masked placeholder for a secret PasswordField: a dotted tail when set, else "not configured".
function secretPlaceholder(status: SecretStatus, t: ReturnType<typeof useTranslate>): string {
  if (!status.configured) return t("instellingen.integraties.secret.empty");
  return status.last4 ? `•••• ${status.last4}` : "••••••••";
}

function CardShell({
  title,
  status,
  children,
  onSave,
  saving,
}: {
  title: string;
  status: React.ReactNode;
  children: React.ReactNode;
  onSave: () => void;
  saving: boolean;
}) {
  const t = useTranslate();
  return (
    <div className="settings-integrations-card">
      <div className="settings-integrations-card-header">
        <span className="settings-integrations-card-title">{title}</span>
        {status}
      </div>
      {children}
      <div className="settings-integrations-card-actions">
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}

function IdealCard({
  vestigingId,
  initial,
  onSaved,
}: {
  vestigingId: number;
  initial: IdealConfig;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [apiKey, setApiKey] = useState("");
  const [profileId, setProfileId] = useState(initial.profileId);
  const [methods, setMethods] = useState(initial.methods);
  const [webhook, setWebhook] = useState(initial.webhook);
  const [active, setActive] = useState(initial.active);

  function save() {
    mutate(
      {
        url: `instellingen/integraties/ideal?vestigingId=${vestigingId}`,
        method: "put",
        values: { apiKey, profileId, methods, webhook, active },
      },
      { onSuccess: () => onSaved() },
    );
  }

  return (
    <CardShell title={t("instellingen.item.idealMollie")} status={<SecretStatusChip status={initial.apiKey} />} onSave={save} saving={isLoading}>
      <PasswordField
        label={t("instellingen.integraties.field.apiKey")}
        value={apiKey}
        placeholder={secretPlaceholder(initial.apiKey, t)}
        onChange={(e) => setApiKey(e.target.value)}
      />
      <Field label={t("instellingen.integraties.field.profileId")} value={profileId} onChange={(e) => setProfileId(e.target.value)} />
      <Field label={t("instellingen.integraties.field.methods")} value={methods} onChange={(e) => setMethods(e.target.value)} />
      <Field label={t("instellingen.integraties.field.webhook")} value={webhook} onChange={(e) => setWebhook(e.target.value)} />
      <ToggleField label={t("instellingen.integraties.field.active")} checked={active} onChange={(e) => setActive(e.target.checked)} />
    </CardShell>
  );
}

function PinCard({
  vestigingId,
  initial,
  onSaved,
}: {
  vestigingId: number;
  initial: PinConfig;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [provider, setProvider] = useState<PinProviderKey>(
    (PIN_PROVIDERS.includes(initial.provider as PinProviderKey) ? initial.provider : "CCV") as PinProviderKey,
  );
  const providerConfig = (p: PinProviderKey): PinProviderConfig =>
    p === "Adyen" ? initial.adyen : p === "SumUp" ? initial.sumup : initial.ccv;

  // Re-seed the form when the provider switches: the secret always starts blank (kept unless typed);
  // the identifier + active toggle reflect the selected provider's stored values.
  const [apiKey, setApiKey] = useState("");
  const [identifier, setIdentifier] = useState(providerConfig(provider).identifier);
  const [active, setActive] = useState(providerConfig(provider).active);

  function switchProvider(next: PinProviderKey) {
    setProvider(next);
    setApiKey("");
    setIdentifier(providerConfig(next).identifier);
    setActive(providerConfig(next).active);
  }

  const identifierLabelKey =
    provider === "Adyen"
      ? "instellingen.integraties.field.merchantAccount"
      : provider === "SumUp"
        ? "instellingen.integraties.field.clientId"
        : "instellingen.integraties.field.merchantId";

  function save() {
    mutate(
      {
        url: `instellingen/integraties/pin?vestigingId=${vestigingId}`,
        method: "put",
        values: { provider, apiKey, identifier, active },
      },
      { onSuccess: () => onSaved() },
    );
  }

  return (
    <CardShell
      title={t("instellingen.item.pinapparaat")}
      status={<SecretStatusChip status={providerConfig(provider).apiKey} />}
      onSave={save}
      saving={isLoading}
    >
      <SelectField
        label={t("instellingen.integraties.field.provider")}
        value={provider}
        onChange={(e) => switchProvider(e.target.value as PinProviderKey)}
        options={PIN_PROVIDERS.map((p) => ({ value: p, label: p }))}
      />
      <PasswordField
        label={t("instellingen.integraties.field.apiKey")}
        value={apiKey}
        placeholder={secretPlaceholder(providerConfig(provider).apiKey, t)}
        onChange={(e) => setApiKey(e.target.value)}
      />
      <Field label={t(identifierLabelKey)} value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
      <ToggleField label={t("instellingen.integraties.field.active")} checked={active} onChange={(e) => setActive(e.target.checked)} />
    </CardShell>
  );
}

function PetbaseCard({
  vestigingId,
  initial,
  onSaved,
}: {
  vestigingId: number;
  initial: PetbaseConfig;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [username, setUsername] = useState(initial.username);
  const [password, setPassword] = useState("");
  const [locale, setLocale] = useState(initial.locale);
  const [active, setActive] = useState(initial.active);

  function save() {
    mutate(
      {
        url: `instellingen/integraties/petbase?vestigingId=${vestigingId}`,
        method: "put",
        values: { username, password, locale, active },
      },
      { onSuccess: () => onSaved() },
    );
  }

  return (
    <CardShell title={t("instellingen.item.petbase")} status={<SecretStatusChip status={initial.password} />} onSave={save} saving={isLoading}>
      <Field label={t("instellingen.integraties.field.username")} value={username} onChange={(e) => setUsername(e.target.value)} />
      <PasswordField
        label={t("instellingen.integraties.field.password")}
        value={password}
        placeholder={secretPlaceholder(initial.password, t)}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Field label={t("instellingen.integraties.field.locale")} value={locale} onChange={(e) => setLocale(e.target.value)} />
      <ToggleField label={t("instellingen.integraties.field.active")} checked={active} onChange={(e) => setActive(e.target.checked)} />
    </CardShell>
  );
}

/** The branch-context Koppelingen tile body: a grid of the three integration cards. */
export function IntegrationsManagement({ vestigingId }: { vestigingId: number }) {
  const t = useTranslate();
  // Relative path: the dataProvider's `custom` prepends the /api base; the query string carries the
  // branch scope (the `custom` adapter passes `url` through verbatim, query params included).
  const { data, isLoading, isError, refetch } = useCustom<IntegratiesResponse>({
    url: `instellingen/integraties?vestigingId=${vestigingId}`,
    method: "get",
  });

  if (isLoading) {
    return <div className="settings-info-banner">{t("common.loading")}</div>;
  }
  if (isError || !data?.data) {
    return <div className="settings-info-banner">{t("instellingen.integraties.loadError")}</div>;
  }

  const cfg = data.data;
  const onSaved = () => {
    void refetch();
  };

  // Key each card on its data slice: a successful save triggers refetch, the slice changes, and the
  // card remounts so its form re-seeds from the canonical server state (incl. the refreshed mask).
  return (
    <div className="settings-integrations-grid">
      <IdealCard key={`ideal-${JSON.stringify(cfg.ideal)}`} vestigingId={vestigingId} initial={cfg.ideal} onSaved={onSaved} />
      <PinCard key={`pin-${JSON.stringify(cfg.pin)}`} vestigingId={vestigingId} initial={cfg.pin} onSaved={onSaved} />
      <PetbaseCard key={`petbase-${JSON.stringify(cfg.petbase)}`} vestigingId={vestigingId} initial={cfg.petbase} onSaved={onSaved} />
    </div>
  );
}
